import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid2,
  Typography,
  useTheme,
} from '@mui/material';
import {
  bootstrap,
  MediaElement,
  RibbonElement,
  TypographyElement,
} from '@plugins/next-cms-core';
import { DateTime } from 'luxon';
import { useTranslation } from 'next-i18next';
import Link from '@components/atoms/Link';
import Image from '@components/atoms/Image';
import { components } from '../../../cmsconfig';

export default function PageItem(props) {
  const {
    pageType,
    url,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  let Content = PageContent;

  if (pageType === 'blogItem') Content = BlogContent;
  else if (pageType === 'campaignItem') Content = CampaignContent;

  return (
    <Card
      sx={{
        '& *': {
          textDecoration: 'none !important',
        },
      }}
    >
      <Link href={url}>
        <CardActionArea>
          <Content t={t} theme={theme} {...props} url={url} />
        </CardActionArea>
      </Link>
    </Card>
  );
}

function CampaignContent(props) {
  const {
    t,
    theme,
    name,
    publishDate,
    publishedAt,
    blogPreviewMedia,
    blogPreviewMediaRibbon,
    blogPreviewText,
  } = props;
  const date = DateTime.fromISO(publishDate ?? publishedAt);

  return (
    <Grid2 alignItems="stretch" container>
      {blogPreviewMedia && (
        <Grid2
          size={{ sm: 6, xs: 12 }}
          sx={{
            position: 'relative',
            [theme.breakpoints.down('xs')]: {
              marginBottom: theme.spacing(2),
            },
            [theme.breakpoints.up('sm')]: {
              marginRight: theme.spacing(2),
            },
          }}
        >
          <MediaElement
            data={blogPreviewMedia}
            isFluid
            style={{
              flex: 1,
              objectFit: 'cover',
              [theme.breakpoints.down('xs')]: {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              [theme.breakpoints.up('sm')]: {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              },
            }}
          />
          {blogPreviewMediaRibbon && (
            <RibbonElement data={blogPreviewMediaRibbon} />
          )}
        </Grid2>
      )}
      <Grid2
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(3),
        }}
      >
        <Box alignItems="center" display="flex">
          <Box mr={1}>
            <Chip color="primary" label={t('components.molecules.Search.PageItem.action')} size="small" />
          </Box>
          <Typography color="primary" variant="body2">
            {date.toFormat('dd.MM.yyyy')}
          </Typography>
        </Box>
        <Typography component="h4" variant="h5">
          {name}
        </Typography>
        <Box my={3}>
          {blogPreviewText && (
            <TypographyElement data={blogPreviewText} />
          )}
        </Box>
      </Grid2>
    </Grid2>
  );
}

function BlogContent(props) {
  const {
    theme,
    name,
    publishDate,
    publishedAt,
    blogPreviewMedia,
    blogPreviewMediaRibbon,
    blogPreviewText,
  } = props;
  const date = DateTime.fromISO(publishDate ?? publishedAt);

  return (
    <Grid2 alignItems="stretch" container>
      {blogPreviewMedia && (
        <Grid2
          size={{ sm: 3, xs: 12 }}
          sx={{
            position: 'relative',
            [theme.breakpoints.down('xs')]: {
              marginBottom: theme.spacing(2),
            },
            [theme.breakpoints.up('sm')]: {
              marginRight: theme.spacing(2),
            },
          }}
        >
          <MediaElement
            data={blogPreviewMedia}
            isFluid
            style={{
              flex: 1,
              objectFit: 'cover',
              [theme.breakpoints.down('xs')]: {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              [theme.breakpoints.up('sm')]: {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              },
            }}
          />
          {blogPreviewMediaRibbon && (
            <RibbonElement data={blogPreviewMediaRibbon} />
          )}
        </Grid2>
      )}
      <Grid2
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(3),
        }}
      >
        <Box alignItems="center" display="flex">
          <Box mr={1}>
            <Chip color="primary" label="News" size="small" />
          </Box>
          <Typography color="primary" variant="body2">
            {date.toFormat('dd.MM.yyyy')}
          </Typography>
        </Box>
        <Typography component="h4" variant="h5">
          {name}
        </Typography>
        <Box my={3}>
          {blogPreviewText && (
            <TypographyElement data={blogPreviewText} />
          )}
        </Box>
      </Grid2>
    </Grid2>
  );
}

function PageContent(props) {
  const {
    metaPageTitle,
    metaPageDescription,
    content,
    theme,
  } = props;
  let title = {
    display_variant: 'h5',
    value: metaPageTitle,
  };
  let subtitle = null;
  let previewImage = null;
  let text = {
    value: metaPageDescription,
  };

  const pageHeaderBlock = findContentElement(content, 'ComponentContentPageHeader');

  if (pageHeaderBlock && pageHeaderBlock.pageHeaderSlides.length > 0) {
    const slide = pageHeaderBlock.pageHeaderSlides[0];

    if (slide.media?.file?.data) {
      previewImage = slide.media.file.data.attributes.url;
    }

    if (slide.title?.value) {
      title = {
        displayVariant: 'h5',
        value: slide.title.value,
      };
    }

    if (slide.subtitle?.value) {
      subtitle = {
        displayVariant: 'body2',
        value: slide.subtitle.value,
      };
    }
  }

  const textMediaBlock = findContentElement(content, 'ComponentContentTextMedia');
  const textBlock = findContentElement(content, 'ComponentContentText');

  if (textMediaBlock?.textMediaText?.value) {
    text = {
      value: textMediaBlock.textMediaText.value,
    };
  } else if (textBlock?.textBlockTextColumns.length > 0) {
    const column1 = textBlock.textBlockTextColumns[0];

    if (column1.value) {
      text = {
        value: column1.value,
      };
    }
  }

  return (
    <Grid2 alignItems="stretch" container>
      {previewImage && (
        <Grid2
          size={{ sm: 3, xs: 12 }}
          sx={{
            position: 'relative',
            [theme.breakpoints.down('xs')]: {
              marginBottom: theme.spacing(2),
            },
            [theme.breakpoints.up('sm')]: {
              marginRight: theme.spacing(2),
            },
          }}
        >
          <Image
            alt={metaPageTitle}
            isFluid
            isSquare
            src={previewImage}
            styles={{
              flex: 1,
              objectFit: 'cover',
              [theme.breakpoints.down('xs')]: {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              [theme.breakpoints.up('sm')]: {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              },
            }}
          />
        </Grid2>
      )}
      <Grid2
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(3),
        }}
      >
        <TypographyElement
          data={title}
        />
        {subtitle && (
          <TypographyElement
            data={subtitle}
          />
        )}
        <Box
          my={3}
          sx={{
            overflow: 'hidden',
            maxHeight: 140,
            position: 'relative',
            '&::after': {
              content: '""',
              display: 'block',
              position: 'absolute',
              left: 0,
              width: '100%',
              bottom: 0,
              height: 50,
              background: 'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
            },
          }}
        >
          <TypographyElement
            data={text}
          />
        </Box>
      </Grid2>
    </Grid2>
  );
}

function findContentElement(content, type) {
  // eslint-disable-next-line no-underscore-dangle
  return content.find((e) => e.__typename === type);
}

function truncate(input, length) {
  if (input.length > length) {
    return `${input.substring(0, length)}…`;
  }
  return input;
}

const { graphQlFragments } = bootstrap({
  components,
});

PageItem.graphQlProps = `
  id
  attributes {
    url
    search_score
    metaPageTitle
    metaPageDescription
    pageType
    slug
    name
    publishedAt
    publishDate
    isPinnedToTop
    blogPreviewText {
      ${TypographyElement.graphQlSchema}
    }
    blogPreviewMedia {
      ${MediaElement.graphQlSchema}
    }
    blogPreviewMediaRibbon {
      ${RibbonElement.graphQlSchema}
    }
    content {
      ${graphQlFragments}
    }
  }
`;
